import { useEffect, useState, useCallback, Fragment } from "react";
import api from "@/lib/api";
import { useAuth, isCEO } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { SPEC_KEYS, fmtNum, formatApiError } from "@/lib/utils";
import { Trash2, RotateCcw } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const isCEORole = isCEO(user);
  const [batches, setBatches] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [tab, setTab] = useState("production");
  const [confirm, setConfirm] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const [b, d] = await Promise.all([api.get("/batches"), api.get("/dispatches")]);
    setBatches(b.data);
    setDispatches(d.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeleteDispatch = async (d) => {
    setErr("");
    try {
      const res = await api.delete(`/dispatches/${d.id}`);
      setConfirm(null);
      await load();
      const restored = res.data.restored || [];
      if (restored.length > 0) {
        const total = restored.reduce((s, r) => s + r.bags_restored, 0);
        setErr(`✓ Dispatch deleted. ${total} bags restored to stock across ${restored.length} batch(es).`);
      }
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const [expandedProdKey, setExpandedProdKey] = useState(null);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  const [deletingGroupKey, setDeletingGroupKey] = useState(null);

  const handleDeleteBatch = async (b) => {
    setErr("");
    if (!window.confirm(`Delete production batch ${b.batch_number}?\n\n${b.bags_count} bags · ${b.bags_count * 50} kg\nDate: ${b.production_date} · Shift: ${b.shift}\n\nRecoverable from Recently Deleted.`)) return;
    setDeletingBatchId(b.id);
    try {
      await api.delete(`/batches/${b.id}`);
      await load();
      setExpandedProdKey(null);
      setErr(`✓ Batch ${b.batch_number} moved to Recently Deleted.`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setDeletingBatchId(null);
    }
  };

  const handleDeleteGroup = async (r, groupBatches) => {
    setErr("");
    if (groupBatches.length === 0) return;
    const summary = `Delete ALL ${groupBatches.length} production batch(es) for ${r.date} · ${r.shift.toUpperCase()}?\n\nTotal: ${r.bags} bags · ${r.kg} kg\n\nBatches: ${groupBatches.map((b) => b.batch_number).join(", ")}\n\nAll are recoverable from Recently Deleted.`;
    if (!window.confirm(summary)) return;
    const key = `${r.date}-${r.shift}`;
    setDeletingGroupKey(key);
    const failed = [];
    for (const b of groupBatches) {
      try {
        await api.delete(`/batches/${b.id}`);
      } catch (e) {
        failed.push(`${b.batch_number} (${formatApiError(e)})`);
      }
    }
    await load();
    setExpandedProdKey(null);
    setDeletingGroupKey(null);
    if (failed.length === 0) {
      setErr(`✓ ${groupBatches.length} batch(es) for ${r.date} ${r.shift} moved to Recently Deleted.`);
    } else if (failed.length < groupBatches.length) {
      setErr(`⚠ Deleted ${groupBatches.length - failed.length} of ${groupBatches.length}. Failed: ${failed.join("; ")}`);
    } else {
      setErr(`✗ Could not delete any batch: ${failed.join("; ")}`);
    }
  };

  // Group production by date + shift
  const prodGrouped = {};
  for (const b of batches) {
    if (b.is_mix) continue;
    const key = `${b.production_date}|${b.shift}`;
    if (!prodGrouped[key]) prodGrouped[key] = { date: b.production_date, shift: b.shift, batches: 0, bags: 0, kg: 0 };
    prodGrouped[key].batches += 1;
    prodGrouped[key].bags += b.bags_count;
    prodGrouped[key].kg += b.bags_count * 50;
  }
  const prodRows = Object.values(prodGrouped).sort((a, b) => b.date.localeCompare(a.date) || a.shift.localeCompare(b.shift));

  // Weighted avg specs across approved stock
  const approved = batches.filter((b) => b.status === "approved" && b.specs);
  const avgSpecs = {};
  for (const s of SPEC_KEYS) {
    let sum = 0, w = 0;
    for (const b of approved) {
      const v = b.specs?.[s.key];
      if (v === null || v === undefined) continue;
      sum += Number(v) * b.bags_remaining;
      w += b.bags_remaining;
    }
    avgSpecs[s.key] = w > 0 ? Math.round((sum / w) * 1000) / 1000 : null;
  }

  return (
    <div className="p-4 sm:p-8">
      <PageHeader overline="ANALYTICS" title="Reports" description="Production by shift, stock specifications, and dispatch register." />

      <div className="flex gap-1 mb-6 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        {[
          { id: "production", label: "Production Report" },
          { id: "specs", label: "Stock Spec Summary" },
          { id: "dispatch", label: "Dispatch Register" },
        ].map((t) => (
          <button
            key={t.id}
            className="px-4 py-2 text-sm transition-colors whitespace-nowrap"
            style={{
              color: tab === t.id ? "var(--text)" : "var(--text-muted)",
              borderBottom: tab === t.id ? "2px solid var(--orange)" : "2px solid transparent",
              marginBottom: "-1px",
              fontWeight: tab === t.id ? 600 : 400,
            }}
            onClick={() => setTab(t.id)}
            data-testid={`report-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && (
        <div
          className="card p-3 mb-4 text-xs"
          style={{ borderColor: err.startsWith("✓") ? "var(--green)" : "var(--red)", color: err.startsWith("✓") ? "var(--green)" : "var(--red)" }}
          data-testid="production-report-msg"
        >
          {err}
        </div>
      )}

      {tab === "production" && (
        <div className="card overflow-hidden">
          <div style={{ overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  {isCEORole && <th style={{ width: 30 }}></th>}
                  <th>Date</th>
                  <th>Shift</th>
                  <th className="text-right">Batches</th>
                  <th className="text-right">Bags</th>
                  <th className="text-right">KG</th>
                  {isCEORole && <th className="text-right" style={{ width: 60 }}>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {prodRows.length === 0 && <tr><td colSpan={isCEORole ? 7 : 5} className="text-center py-8" style={{ color: "var(--text-muted)" }}>No data.</td></tr>}
                {prodRows.map((r) => {
                  const key = `${r.date}-${r.shift}`;
                  const isOpen = expandedProdKey === key;
                  const rowBatches = batches.filter((b) => !b.is_mix && b.production_date === r.date && b.shift === r.shift);
                  return (
                    <Fragment key={key}>
                      <tr
                        onClick={() => isCEORole && setExpandedProdKey(isOpen ? null : key)}
                        style={{ cursor: isCEORole ? "pointer" : "default" }}
                        data-testid={`prod-row-${key}`}
                      >
                        {isCEORole && (
                          <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>{isOpen ? "▾" : "▸"}</td>
                        )}
                        <td className="font-mono">{r.date}</td>
                        <td className="font-mono uppercase text-xs">{r.shift}</td>
                        <td className="font-mono text-right">{r.batches}</td>
                        <td className="font-mono text-right">{fmtNum(r.bags)}</td>
                        <td className="font-mono text-right">{fmtNum(r.kg)}</td>
                        {isCEORole && (
                          <td className="text-right">
                            <button
                              className="btn btn-ghost"
                              onClick={(e) => { e.stopPropagation(); handleDeleteGroup(r, rowBatches); }}
                              disabled={deletingGroupKey === key}
                              style={{ padding: "4px 8px", color: "var(--red)" }}
                              title={`Delete all ${rowBatches.length} batch(es) in this group (recoverable)`}
                              data-testid={`delete-prod-row-${key}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                      {isCEORole && isOpen && (
                        <tr key={`${key}-detail`}>
                          <td colSpan={7} style={{ background: "var(--bg)", padding: 0 }}>
                            <div className="p-4">
                              <div className="text-xs font-mono mb-2" style={{ color: "var(--text-muted)" }}>
                                // BATCHES IN {r.date} {r.shift.toUpperCase()} — CLICK TRASH TO DELETE (recoverable)
                              </div>
                              <table className="table" style={{ background: "var(--surface)" }}>
                                <thead>
                                  <tr>
                                    <th>Batch #</th>
                                    <th>Location</th>
                                    <th>Status</th>
                                    <th className="text-right">Bags</th>
                                    <th className="text-right">Remaining</th>
                                    <th>Supplier / Notes</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rowBatches.map((b) => (
                                    <tr key={b.id} data-testid={`prod-batch-${b.batch_number}`}>
                                      <td className="font-mono">{b.batch_number}</td>
                                      <td className="font-mono text-xs uppercase">{b.location || "FACTORY"}</td>
                                      <td className="font-mono text-xs">{b.status}</td>
                                      <td className="font-mono text-right">{fmtNum(b.bags_count)}</td>
                                      <td className="font-mono text-right">{fmtNum(b.bags_remaining)}</td>
                                      <td className="text-xs" style={{ color: "var(--text-muted)" }}>{b.supplier_company || b.notes || "—"}</td>
                                      <td>
                                        <button
                                          className="btn btn-ghost"
                                          onClick={(e) => { e.stopPropagation(); handleDeleteBatch(b); }}
                                          disabled={deletingBatchId === b.id}
                                          style={{ padding: "4px 8px", color: "var(--red)" }}
                                          title="Delete this batch (recoverable)"
                                          data-testid={`delete-prod-batch-${b.batch_number}`}
                                        >
                                          <Trash2 size={12} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "specs" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SPEC_KEYS.map((s) => (
            <div key={s.key} className="card p-5">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">
                {avgSpecs[s.key] !== null ? avgSpecs[s.key] : "—"}
                <span className="text-sm font-mono ml-1" style={{ color: "var(--text-muted)" }}>{s.suffix}</span>
              </div>
              <div className="stat-sub">weighted avg across approved stock</div>
            </div>
          ))}
        </div>
      )}

      {tab === "dispatch" && (
        <div className="card overflow-hidden">
          <div style={{ overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Vehicle</th>
                  <th>Dispatch Batch</th>
                  <th>Batches Picked</th>
                  <th className="text-right">Bags</th>
                  <th className="text-right">KG</th>
                  <th>By</th>
                  {isCEORole && <th></th>}
                </tr>
              </thead>
              <tbody>
                {dispatches.length === 0 && <tr><td colSpan={isCEORole ? 9 : 8} className="text-center py-8" style={{ color: "var(--text-muted)" }}>No dispatches.</td></tr>}
                {dispatches.map((d) => (
                  <tr key={d.id}>
                    <td className="font-mono text-xs">{d.created_at.slice(0, 16).replace("T", " ")}</td>
                    <td>{d.customer}</td>
                    <td className="font-mono">{d.vehicle || "—"}</td>
                    <td className="font-mono">{d.dispatch_batch_number || "—"}</td>
                    <td className="font-mono text-xs">{d.items.map((i) => i.batch_number).join(", ")}</td>
                    <td className="font-mono text-right">{fmtNum(d.total_bags)}</td>
                    <td className="font-mono text-right">{fmtNum(d.total_kg)}</td>
                    <td className="text-xs" style={{ color: "var(--text-muted)" }}>{d.created_by_name}</td>
                    {isCEORole && (
                      <td>
                        <button
                          className="btn btn-ghost"
                          style={{ padding: "4px 8px", fontSize: "11px", color: "var(--red)" }}
                          onClick={() => { setConfirm(d); setErr(""); }}
                          data-testid={`delete-dispatch-${d.id}`}
                          title="Delete & restore bags (CEO only)"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirm dispatch delete */}
      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setConfirm(null)}
        >
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-dispatch-modal">
            <div className="text-xs font-mono mb-2" style={{ color: "var(--red)" }}>// CEO OVERRIDE — DELETE DISPATCH</div>
            <h3 className="font-heading text-xl font-bold mb-3">Delete this dispatch?</h3>
            <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              You are about to delete the dispatch to{" "}
              <span style={{ color: "var(--text)" }}>{confirm.customer}</span> on{" "}
              <span className="font-mono">{confirm.created_at.slice(0,10)}</span>{" "}
              ({confirm.total_bags} bags / {confirm.total_kg} kg).
            </div>
            <div className="text-xs p-2 mb-4 flex items-start gap-2" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid var(--green)", color: "var(--green)" }}>
              <RotateCcw size={14} className="flex-shrink-0 mt-0.5" />
              <span>Bags will be automatically restored to their source batches.</span>
            </div>
            <div className="flex gap-3">
              <button className="btn btn-secondary flex-1" onClick={() => setConfirm(null)} data-testid="cancel-delete-dispatch-button">Cancel</button>
              <button className="btn btn-danger flex-1" onClick={() => handleDeleteDispatch(confirm)} data-testid="confirm-delete-dispatch-button">
                <Trash2 size={14} /> Delete & Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
